'use client';

import dynamic from 'next/dynamic';
import { useCallback, useMemo } from 'react';
import useAdvancedMapLayersPeakValidation from '@alltrails/advanced-map-layers/hooks/useAdvancedMapLayersPeakValidation';
import useFallFoliageTierValidation from '@alltrails/advanced-map-layers/hooks/useFallFoliageTierValidation';
import type UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import useConditionsValidation from '@alltrails/conditions/hooks/useConditionsValidation';
import { CONDITIONS_FEATURE_ENTITLEMENTS } from '@alltrails/context/hooks/useFeatureEntitlements';
import useHeatmapsTierValidation from '@alltrails/heatmaps/hooks/useHeatmapsTierValidation';
import type { BaseStyleId, DynamicStyleId } from '@alltrails/maps/types/Styles';
import type { FeatureEntitlement } from '@alltrails/types/FeatureEntitlement';

const AdvancedMapsUpsellModal = dynamic(() => import('@alltrails/advanced-map-layers/components/AdvancedMapsUpsellModal/AdvancedMapsUpsellModal'), {
  ssr: false
});
const ConditionsUpsellModal = dynamic(() => import('@alltrails/conditions/components/ConditionsUpsellModal'), { ssr: false });
const FallFoliageUpsellModal = dynamic(() => import('@alltrails/advanced-map-layers/components/FallFoliageUpsellModal/FallFoliageUpsellModal'), {
  ssr: false
});
const HeatmapsUpsellModal = dynamic(() => import('@alltrails/heatmaps/components/HeatmapsUpsellModal'), { ssr: false });

type UseLayerUpsellsArgs = {
  /** Which surface the heatmap upsell reports itself from. */
  heatmapsUpsellTrigger: UpsellTrigger;
};

/**
 * Plus/Peak gating for every layer menu in this app: the handler `useStyleSelection` calls when a style
 * needs an entitlement the user lacks, plus the modals it opens. A surface that can't apply a given layer
 * never reaches its branch, so there is nothing to opt out of — `supportedDynamicStyleIds` already decides
 * which of these can fire.
 */
const useLayerUpsells = ({ heatmapsUpsellTrigger }: UseLayerUpsellsArgs) => {
  const { showConditionsUpsellModal, onConditionsUpsellModalCloseRequest, handleConditionsFailedEntitlementCheck } = useConditionsValidation();
  const { showHeatmapsUpsellModal, onHeatmapsUpsellModalCloseRequest, handleHeatmapFailedEntitlementCheck } = useHeatmapsTierValidation();
  const { showFoliageUpsellModal, onFoliageUpsellModalCloseRequest, handleFoliageFailedEntitlementCheck } = useFallFoliageTierValidation();
  const { advancedMapsUpsellTrigger, handleAdvancedMapLayersEntitlementCheck, onUpsellModalCloseRequest, showAdvancedMapsUpsellModal } =
    useAdvancedMapLayersPeakValidation();

  const handleFailedEntitlementCheck = useCallback(
    (entitlement: FeatureEntitlement, applyStyle: () => void, styleId?: DynamicStyleId | BaseStyleId) => {
      if (CONDITIONS_FEATURE_ENTITLEMENTS.includes(entitlement)) {
        handleConditionsFailedEntitlementCheck(applyStyle);
      } else if (entitlement === 'community_heat_maps') {
        handleHeatmapFailedEntitlementCheck(applyStyle);
      } else if (entitlement === 'advanced_map_layers' && styleId) {
        handleAdvancedMapLayersEntitlementCheck(styleId, applyStyle);
      } else if (entitlement === 'fall_foliage') {
        handleFoliageFailedEntitlementCheck(applyStyle);
      }
    },
    [
      handleAdvancedMapLayersEntitlementCheck,
      handleConditionsFailedEntitlementCheck,
      handleFoliageFailedEntitlementCheck,
      handleHeatmapFailedEntitlementCheck
    ]
  );

  const upsellModals = useMemo(
    () => (
      <>
        <ConditionsUpsellModal
          isOpen={showConditionsUpsellModal}
          onCloseRequest={onConditionsUpsellModalCloseRequest}
          upsellTrigger="advanced conditions layer tapped"
        />
        <HeatmapsUpsellModal
          isOpen={showHeatmapsUpsellModal}
          onCloseRequest={onHeatmapsUpsellModalCloseRequest}
          upsellTrigger={heatmapsUpsellTrigger}
        />
        <AdvancedMapsUpsellModal
          isOpen={showAdvancedMapsUpsellModal}
          onCloseRequest={onUpsellModalCloseRequest}
          upsellTrigger={advancedMapsUpsellTrigger}
        />
        <FallFoliageUpsellModal isOpen={showFoliageUpsellModal} onCloseRequest={onFoliageUpsellModalCloseRequest} />
      </>
    ),
    [
      advancedMapsUpsellTrigger,
      heatmapsUpsellTrigger,
      onConditionsUpsellModalCloseRequest,
      onFoliageUpsellModalCloseRequest,
      onHeatmapsUpsellModalCloseRequest,
      onUpsellModalCloseRequest,
      showAdvancedMapsUpsellModal,
      showConditionsUpsellModal,
      showFoliageUpsellModal,
      showHeatmapsUpsellModal
    ]
  );

  const isUpsellOpen = showConditionsUpsellModal || showHeatmapsUpsellModal || showAdvancedMapsUpsellModal || showFoliageUpsellModal;

  return useMemo(() => ({ handleFailedEntitlementCheck, isUpsellOpen, upsellModals }), [handleFailedEntitlementCheck, isUpsellOpen, upsellModals]);
};

export default useLayerUpsells;
